import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  session: Session | null;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, providerIntent?: boolean, referralCode?: string, promoCode?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithDiscord: () => Promise<void>;
  signInWithTwitter: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getRedirectUrl = () => {
    return Capacitor.isNativePlatform()
      ? 'io.petius.app://callback/auth'
      : `${window.location.origin}/auth`;
  };

  // Sync OAuth metadata with database on auth state change
  const syncOAuthData = async (currentUser: User) => {
    try {
      const metadata = currentUser.user_metadata;
      
      // Extract name and avatar from OAuth metadata
      const fullName = metadata?.name || metadata?.full_name || 
                      `${metadata?.given_name || ''} ${metadata?.family_name || ''}`.trim();
      const avatarUrl = metadata?.avatar_url || metadata?.picture || 
                       metadata?.picture_url || metadata?.image_url;

      // Only update if we have valid OAuth data
      if (fullName && fullName.includes(' ')) {
        // Update users table
        await supabase
          .from('users')
          .update({ 
            full_name: fullName,
            avatar_url: avatarUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentUser.id);

      // Update profiles table
        await supabase
          .from('profiles')
          .upsert({ 
            user_id: currentUser.id,
            display_name: fullName,
            avatar_url: avatarUrl,
            provider_intent: currentUser.user_metadata?.provider_intent || false,
            updated_at: new Date().toISOString()
          }, { 
            onConflict: 'user_id' 
          });
      }
    } catch (error) {
      console.error('Error syncing OAuth data:', error);
    }
  };

  const redeemPendingPromoCode = async (currentUser: User) => {
    try {
      const promoCode = currentUser.user_metadata?.promo_code as string | undefined;
      if (!promoCode) return;

      // Provider promo codes are redeemed after the business profile is created.
      // Keep the code in user_metadata so register-provider can apply it.
      const context = currentUser.user_metadata?.provider_intent ? 'provider' : 'user';
      if (context === 'provider') return;

      const { data, error } = await supabase.functions.invoke('redeem-code', {
        body: { code: promoCode, context }
      });

      if (error) {
        console.error('Error redeeming promo code:', error);
        return;
      }

      if (data?.success) {
        // Clear the promo code from metadata so it isn't redeemed again
        await supabase.auth.updateUser({
          data: { promo_code: null }
        });
      }
    } catch (error) {
      console.error('Error in redeemPendingPromoCode:', error);
    }
  };

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION on subscribe, so we don't need
    // a separate getSession() call — calling both triggers duplicate state
    // updates and makes the UI feel like it "double refreshes" on navigation.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('🔐 Auth event:', event, newSession?.user?.id);

        setSession((prev) => (prev?.access_token === newSession?.access_token ? prev : newSession));
        setUser((prev) => {
          const next = newSession?.user ?? null;
          if (prev?.id === next?.id) return prev;
          return next;
        });
        setIsLoading(false);

        // Handle password recovery event — route user to update password page
        if (event === 'PASSWORD_RECOVERY') {
          console.log('🔑 PASSWORD_RECOVERY event detected, routing to /update-password');
          window.location.href = '/update-password';
          return;
        }

        // Close In-App Browser on native OAuth login so it doesn't stay open on top of the app
        if (event === 'SIGNED_IN' && newSession?.user) {
          if (Capacitor.isNativePlatform()) {
            Browser.close().catch(() => {});
          }
          setTimeout(() => {
            syncOAuthData(newSession.user);
            redeemPendingPromoCode(newSession.user);
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string, providerIntent?: boolean, referralCode?: string, promoCode?: string) => {
    const fullName = firstName && lastName ? `${firstName} ${lastName}`.trim() : undefined;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getRedirectUrl(),
        data: {
          ...(fullName ? {
            full_name: fullName,
            first_name: firstName,
            last_name: lastName
          } : {}),
          provider_intent: providerIntent || false,
          referred_by_code: referralCode?.toUpperCase() || null,
          promo_code: promoCode?.toUpperCase() || null
        }
      }
    });

    if (error) {
      throw error;
    }
    // Supabase returns 200 with empty identities when email already exists
    // (anti-enumeration protection). We detect this and surface a proper error.
    if (data.user && data.user.identities?.length === 0) {
      throw new Error('user already registered');
    }
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: getRedirectUrl()
      }
    });

    if (error) {
      throw error;
    }
  };

  // Helper para OAuth en plataformas nativas usando In-App Browser
  const signInWithOAuthProvider = async (
    provider: 'google' | 'azure' | 'discord' | 'twitter' | 'facebook' | 'apple',
    additionalOptions?: Record<string, any>
  ) => {
    if (Capacitor.isNativePlatform()) {
      // En nativo: usar skipBrowserRedirect para obtener la URL
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getRedirectUrl(),
          skipBrowserRedirect: true,
          ...additionalOptions
        },
      });

      if (error) throw error;
      
      // Abrir con In-App Browser de Capacitor
      if (data?.url) {
        console.log('🌐 Opening OAuth URL in In-App Browser:', data.url);
        await Browser.open({ 
          url: data.url,
          presentationStyle: 'popover',
          windowName: '_blank'
        });
      }
    } else {
      // En web: comportamiento normal
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getRedirectUrl(),
          ...additionalOptions
        },
      });
      if (error) throw error;
    }
  };

  const signInWithGoogle = async () => {
    await signInWithOAuthProvider('google', {
      queryParams: { prompt: 'select_account' }
    });
  };

  const signInWithMicrosoft = async () => {
    await signInWithOAuthProvider('azure', {
      scopes: 'email profile'
    });
  };

  const signInWithDiscord = async () => {
    await signInWithOAuthProvider('discord', {
      scopes: 'identify email'
    });
  };

  const signInWithTwitter = async () => {
    await signInWithOAuthProvider('twitter', {
      scopes: 'read users.read tweet.read'
    });
  };

  const signInWithFacebook = async () => {
    await signInWithOAuthProvider('facebook');
  };

  const signInWithApple = async () => {
    await signInWithOAuthProvider('apple');
  };

  const value = {
    user,
    setUser,
    session,
    signOut,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithDiscord,
    signInWithTwitter,
    signInWithFacebook,
    signInWithApple,
    resendVerificationEmail,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};